// Background aura layered behind the role card on the reveal screen.
// Faction defines the base palette; specific roles get a custom flourish
// (e.g. arsonist embers, werewolf moon flicker, sheriff badge halo).

import type { RoleConfig } from '../../../shared/src/types.js';

interface Props {
  role: RoleConfig;
  // 0..2 — matches RoleReveal stage. We use it to gate fancier effects to
  // the moment the card is actually flipped.
  stage: number;
}

export function RoleRevealAura({ role, stage }: Props) {
  const f = role.faction;
  const accent = role.accent;

  return (
    <div className="reveal-aura" aria-hidden>
      {/* Faction-tinted radial wash */}
      <div className={`aura-wash aura-${f}`} />
      {/* Spotlight cone */}
      <div className="aura-cone" style={{ ['--cone' as never]: accent }} />
      {/* Crosshair scan when card lands */}
      {stage >= 1 && (
        <svg className="aura-rings" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
          <circle cx="200" cy="200" r="80" fill="none" stroke={accent} strokeWidth="1.5" opacity="0.6" />
          <circle cx="200" cy="200" r="140" fill="none" stroke={accent} strokeWidth="1" opacity="0.45" />
          <circle cx="200" cy="200" r="190" fill="none" stroke={accent} strokeWidth="0.8" opacity="0.3" />
        </svg>
      )}
      {/* Burst when card flips */}
      {stage >= 2 && (
        <>
          <svg className="aura-burst" viewBox="0 0 400 400">
            {Array.from({ length: 18 }).map((_, i) => {
              const a = (i / 18) * Math.PI * 2;
              return (
                <line
                  key={i}
                  x1={200 + Math.cos(a) * 30}
                  y1={200 + Math.sin(a) * 30}
                  x2={200 + Math.cos(a) * 200}
                  y2={200 + Math.sin(a) * 200}
                  stroke={accent}
                  strokeWidth="1.2"
                  opacity="0.5"
                />
              );
            })}
          </svg>
          <RoleSpecificFlair role={role} />
        </>
      )}
    </div>
  );
}

function RoleSpecificFlair({ role }: { role: RoleConfig }) {
  // A small pool of role-specific embellishments. Roles without an explicit
  // entry fall back to the faction-only aura, which is already plenty.
  switch (role.id) {
    case 'arsonist':
      return <div className="flair-embers" aria-hidden />;
    case 'werewolf':
      return <div className="flair-moon" aria-hidden />;
    case 'sheriff':
    case 'mayor':
      return <div className="flair-halo" style={{ ['--halo' as never]: role.accent }} aria-hidden />;
    case 'doctor':
    case 'paramedic':
      return <div className="flair-pulse" style={{ ['--pulse' as never]: role.accent }} aria-hidden />;
    case 'mafia':
    case 'don':
    case 'hitman':
    case 'goon':
    case 'enforcer':
    case 'consigliere':
      return <div className="flair-smoke" aria-hidden />;
    case 'jester':
      return <div className="flair-confetti" aria-hidden />;
    case 'witch':
    case 'plaguebearer':
      return <div className="flair-tox" aria-hidden />;
    case 'medium':
    case 'psychic':
      return <div className="flair-orb" style={{ ['--orb' as never]: role.accent }} aria-hidden />;
    default:
      return null;
  }
}
